import { Component, DestroyRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VendorChatService } from 'app/services/form/vendor-chat.service';
import { SignalRHubService } from 'app/services/signalr-hub.service';
import { VendorChatMessage } from 'app/models/forms/vendor/vendor-chat-message.model';
import { containsProfanity, PROFANITY_MESSAGE } from 'app/shared/utils/profanity.util';
import { containsLink, LINK_MESSAGE } from 'app/shared/utils/chat-content.util';

@Component({
	selector: 'app-vendor-chat',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './vendor-chat.component.html',
	styleUrls: ['./vendor-chat.component.css'],
})
export class VendorChatComponent implements OnInit, OnDestroy {
	@Input() clientId: string;
	@Input() formId: string;

	messages: VendorChatMessage[] = [];
	unreadCount = 0;
	expanded = false;
	newMessageText = '';
	sending = false;
	loading = true;
	composerError = '';
	reactionPickerFor: number | null = null;
	readonly reactionOptions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

	private readonly destroyRef = inject(DestroyRef);
	private static readonly FORM_TYPE = 'vendor';

	constructor(private readonly vendorChatService: VendorChatService, private readonly signalRHub: SignalRHubService) {}

	ngOnInit(): void {
		if (!this.clientId || !this.formId) {
			this.loading = false;
			return;
		}

		this.loadMessages();
		this.loadUnreadCount();

		this.signalRHub.on('vendorChatMessageReceived', (message: VendorChatMessage) => this.onMessageReceived(message));
		this.signalRHub.on(
			'vendorChatReactionUpdated',
			(reactionEvent: { messageId: number; vendorReaction: string | null; staffReaction: string | null }) => {
				const message = this.messages.find((m) => m.id === reactionEvent.messageId);
				if (message) {
					message.vendorReaction = reactionEvent.vendorReaction;
					message.staffReaction = reactionEvent.staffReaction;
				}
			}
		);
		this.signalRHub
			.whenConnected()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.signalRHub.send('JoinForm', VendorChatComponent.FORM_TYPE, this.formId, 'view').catch(() => {});
			});
	}

	ngOnDestroy(): void {
		// Listeners outlive the component otherwise: the hub keeps them in its
		// pending map and re-attaches them on every reconnect, still pointing at
		// this destroyed instance.
		this.signalRHub.off('vendorChatMessageReceived');
		this.signalRHub.off('vendorChatReactionUpdated');
		this.signalRHub.send('LeaveForm', VendorChatComponent.FORM_TYPE, this.formId).catch(() => {});
	}

	toggle(): void {
		this.expanded = !this.expanded;
		if (this.expanded && this.unreadCount > 0) {
			this.markRead();
		}
	}

	sendMessage(): void {
		const text = this.newMessageText.trim();
		if (!text || this.sending) return;
		if (containsProfanity(text)) {
			this.composerError = PROFANITY_MESSAGE;
			return;
		}
		if (containsLink(text)) {
			this.composerError = LINK_MESSAGE;
			return;
		}
		this.sending = true;
		this.vendorChatService
			.sendMessage(this.clientId, this.formId, text)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (message) => {
					this.appendMessage(message);
					this.newMessageText = '';
					this.sending = false;
					this.composerError = '';
				},
				error: (err) => {
					this.sending = false;
					this.composerError = 'Unable to send the message. Please try again.';
					console.error('Vendor chat send failed:', err);
				},
			});
	}

	onComposerInput(): void {
		if (this.composerError) {
			this.composerError = '';
		}
	}

	toggleReactionPicker(messageId: number): void {
		this.reactionPickerFor = this.reactionPickerFor === messageId ? null : messageId;
	}

	react(message: VendorChatMessage, emoji: string): void {
		const next = message.staffReaction === emoji ? null : emoji;
		this.reactionPickerFor = null;
		this.vendorChatService
			.setReaction(this.clientId, this.formId, message.id, next)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (updated) => {
					message.vendorReaction = updated.vendorReaction;
					message.staffReaction = updated.staffReaction;
				},
				error: () => (this.composerError = 'Unable to update the reaction.'),
			});
	}

	trackById(index: number, message: VendorChatMessage): number {
		return message.id;
	}

	toUtcDate(value: string): string {
		if (!value) return value;
		return value.endsWith('Z') ? value : `${value}Z`;
	}

	private loadMessages(): void {
		this.vendorChatService
			.getMessages(this.clientId, this.formId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (messages) => {
					this.messages = messages;
					this.loading = false;
				},
				error: (err) => {
					this.loading = false;
					console.error('Vendor chat load failed:', err);
				},
			});
	}

	private loadUnreadCount(): void {
		this.vendorChatService
			.getUnreadCount(this.clientId, this.formId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (count) => (this.unreadCount = count),
				error: () => {},
			});
	}

	private onMessageReceived(message: VendorChatMessage): void {
		if (!this.appendMessage(message)) return;
		if (message.senderSide === 'Vendor') {
			if (this.expanded) {
				this.markRead();
			} else {
				this.unreadCount++;
			}
		}
	}

	private appendMessage(message: VendorChatMessage): boolean {
		if (this.messages.some((m) => this.isSameMessage(m, message))) return false;
		this.messages = [...this.messages, message];
		return true;
	}

	// The id alone is not enough: the send response and the SignalR echo of the
	// same stored row can arrive carrying different ids, which showed the
	// sender's own message twice until the next reload.
	private isSameMessage(existing: VendorChatMessage, incoming: VendorChatMessage): boolean {
		if (existing.id && incoming.id && existing.id === incoming.id) return true;
		return (
			existing.senderSide === incoming.senderSide &&
			existing.text === incoming.text &&
			existing.createdDate === incoming.createdDate
		);
	}

	private markRead(): void {
		this.vendorChatService
			.markMessagesRead(this.clientId, this.formId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => (this.unreadCount = 0),
				error: () => {},
			});
	}
}
